import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { PenLine, BookText, SlidersHorizontal, Users, LineChart, MessagesSquare, AudioWaveform, TrendingUp, Wallet, Cog, Layers, FileCode2, Lock, AtSign, Compass, BookOpen, Library, Home, Sparkles, Save, Check, Loader2, LogOut, HelpCircle, FileEdit, AlertTriangle, Map, BookMarked, Globe } from 'lucide-react';
import { useIsGuidanceMode } from './menus/shared';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useCapabilities } from '@/lib/useCapabilities';
import { useForumUnread } from '@/hooks/useForumUnread';
import { useAuth } from '@/lib/auth_context';
import { marketingApi } from '@/lib/api';
import { runTour } from '@/lib/tours';
import { useContextRebuild } from '@/hooks/useContextRebuild';

// Three-Act Structure grouping — Proposal C.
// Left-to-right mirrors the writer's creative journey:
//   Home → Act I (Concept) → Act II (Draft) → Act III (Refine) → Business
const ACT_GROUPS = [
  {
    act: 'I',
    label: 'Concept',
    items: [
      { key: 'home',      icon: Home,             label: 'Home',         guidanceTip: 'Story hub & quick-start tasks', action: null /* special: uses togglePanel */ },
      { key: 'variables', icon: SlidersHorizontal, label: 'Variables',    guidanceTip: 'Track names, places, dates, and facts that must stay consistent' },
      { key: 'bible',     icon: Users,             label: 'Characters',   guidanceTip: 'View and edit your full cast of characters and their relationships' },
      { key: 'plot',      icon: Map,               label: 'Plot Builder', guidanceTip: 'Build your 6-act plot structure and story beats',
        action: () => window.dispatchEvent(new CustomEvent('loom:open-plot-builder')) },
    ],
  },
  {
    act: 'II',
    label: 'Draft',
    items: [
      { key: 'editor',   icon: PenLine,        label: 'Manuscript',  guidanceTip: 'Open the main writing editor for your current chapter' },
      { key: 'chapters', icon: BookText,        label: 'Chapters',    guidanceTip: 'Jump between chapters, reorder, or rename them' },
      { key: 'modes',    icon: Layers,          label: 'Modes',       guidanceTip: 'Switch between Draft, Revise, Line Edit, and other writing modes' },
      { key: 'chat',     icon: MessagesSquare,  label: 'Chat AI',     guidanceTip: 'Have a conversation with the AI about your story', capability: 'claude_chat' },
    ],
  },
  {
    act: 'III',
    label: 'Refine',
    items: [
      { key: 'arc',       icon: LineChart,  label: 'Arc Visualizer',  guidanceTip: "See a graph of your story's emotional and narrative arc" },
      { key: 'plotex',    icon: BookMarked, label: 'Plot Blueprint',  guidanceTip: 'View and edit your AI-extracted plot structure, protagonist arc, and story beats' },
      { key: 'worldex',   icon: Globe,      label: 'World Builder',   guidanceTip: 'View and edit your AI-inferred world settings, era, magic system, society, and geography' },
      { key: 'analysis',  icon: AudioWaveform, label: 'Analysis',     guidanceTip: 'Run deep AI analysis on chapters or your whole novel' },
      { key: 'deviation', icon: AlertTriangle, label: 'Craft Issues', guidanceTip: 'Review AI-flagged craft issues that need your attention' },
    ],
  },
  {
    act: 'Biz',
    label: 'Business',
    items: [
      { key: 'bookshelf',  icon: Library,        label: 'Bookshelf',  guidanceTip: 'View all your manuscripts and their publishability scores' },
      { key: 'marketing',  icon: TrendingUp,     label: 'Marketing',  guidanceTip: 'See reader demand data and marketing opportunities for your book' },
      { key: 'editorial',  icon: FileEdit,       label: 'Editorial',  guidanceTip: 'Request a human editorial review of your manuscript' },
      { key: 'community',  icon: MessagesSquare, label: 'Community',  guidanceTip: 'Talk to other Crown & Rose authors in the live forum' },
    ],
  },
];

// Flat list used only by legacy badge lookups below (no render loop change needed)
const items = ACT_GROUPS.flatMap(g => g.items);

export function ActivityBar({ visible, togglePanel, onBuyTokens, onSettings, onOpenTemplates, onOpenQuickstart, quickstartOpen, onOpenBusiness, onOpenHelp }) {
  const { can, upgradeCta } = useCapabilities();
  const isGuidance = useIsGuidanceMode();
  const { pendingByPanel } = useContextRebuild();
  // iter55z-b — unread + mentions badge on the Community Chat button.
  const { unread, mentions, markRead } = useForumUnread({ authed: true });
  // ── Save Manuscript button state ──────────────────────────────────────────
  const navigate = useNavigate();
  const { logout } = useAuth();
  // Save & Sign Out state
  const [savingOut,          setSavingOut]          = useState(false);
  const [hasUnsavedChanges,  setHasUnsavedChanges]  = useState(false);
  useEffect(() => {
    const onChange = (e) => setHasUnsavedChanges(!!e?.detail?.dirty);
    window.addEventListener('loom:unsaved-changes', onChange);
    return () => window.removeEventListener('loom:unsaved-changes', onChange);
  }, []);
  const handleSaveAndSignOut = async () => {
    if (savingOut) return;
    setSavingOut(true);
    const editorMounted = !!document.querySelector('[data-testid="manuscript-prose"]');
    try {
      const result = await new Promise((resolve) => {
        const onReady = (e) => { window.removeEventListener('loom:save-and-exit-ready', onReady); resolve(e.detail || { ok: true }); };
        window.addEventListener('loom:save-and-exit-ready', onReady);
        window.dispatchEvent(new CustomEvent('loom:save-and-exit'));
        setTimeout(() => { window.removeEventListener('loom:save-and-exit-ready', onReady); resolve({ ok: false, timedOut: true }); }, 2800);
      });
      if (result.ok) toast.success('Saved. Signing out…');
      else if (result.timedOut && !editorMounted) toast.info('Signing out…');
      else toast.warning('Could not confirm save. Signing out anyway.');
      await logout?.();
    } catch (_e) {
      toast.error('Sign-out error — try again.');
    } finally {
      setSavingOut(false);
      navigate('/');
    }
  };
  // Save Manuscript button state.
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const saveTimerRef = useRef(null);
  useEffect(() => {
    const onReady = (e) => {
      const ok = e?.detail?.ok !== false;
      setSaveStatus(ok ? 'saved' : 'error');
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => setSaveStatus('idle'), 1800);
    };
    window.addEventListener('loom:manual-save-ready', onReady);
    return () => {
      window.removeEventListener('loom:manual-save-ready', onReady);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);
  const handleSave = () => {
    if (saveStatus === 'saving') return;
    setSaveStatus('saving');
    window.dispatchEvent(new CustomEvent('loom:manual-save'));
    // If no editor is mounted there's nothing to flush — resolve after a
    // short delay so the button doesn't spin indefinitely.
    setTimeout(() => {
      setSaveStatus((prev) => (prev === 'saving' ? 'saved' : prev));
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => setSaveStatus('idle'), 1800);
    }, 600);
  };
  const [opportunityCount, setOpportunityCount] = useState(0);
  useEffect(() => {
    if (!marketingApi?.opportunityBadge) return;
    const load = () => marketingApi.opportunityBadge({ days: 30 })
      .then((data) => setOpportunityCount(data?.count || 0))
      .catch(() => setOpportunityCount(0));
    load();
    window.addEventListener('marketing:opportunities-updated', load);
    return () => window.removeEventListener('marketing:opportunities-updated', load);
  }, []);
  // Section colour config is no longer used in icon-only sidebar (retained for reference)

  // ── Icon-only narrow sidebar layout ─────────────────────────────────────
  return (
    <div
      className="flex h-full w-12 shrink-0 flex-col bg-chrome border-r border-chrome-border overflow-y-auto [&::-webkit-scrollbar]:w-0"
      data-testid="activity-bar"
    >
      {/* ── Top: Home + Save + Sign Out ─────────────────────────────── */}
      <div className="flex flex-col items-center gap-0.5 border-b border-chrome-border py-2">
        {/* Home */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => { togglePanel('editor', false); togglePanel('home', true); }}
              data-testid="activity-home-btn"
              className={cn(
                'relative flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                visible['home'] ? 'bg-primary/15 text-primary' : 'text-chrome-foreground/70 hover:text-foreground hover:bg-chrome-border',
              )}
            >
              {visible['home'] && <span className="absolute left-0 top-2 bottom-2 w-0.5 rounded-r-full bg-primary" />}
              <Home className="h-[18px] w-[18px]" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-[11px]">Home — story hub</TooltipContent>
        </Tooltip>

        {/* Save */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button" onClick={handleSave} disabled={saveStatus === 'saving'}
              data-testid="activity-save-btn" data-save-status={saveStatus}
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                saveStatus === 'idle'   && 'text-chrome-foreground/70 hover:text-foreground hover:bg-chrome-border',
                saveStatus === 'saving' && 'text-primary opacity-70',
                saveStatus === 'saved'  && 'text-emerald-400 bg-emerald-500/10',
                saveStatus === 'error'  && 'text-red-400',
              )}
            >
              {saveStatus === 'saving' && <Loader2 className="h-[18px] w-[18px] animate-spin" />}
              {saveStatus === 'saved'  && <Check className="h-[18px] w-[18px]" />}
              {(saveStatus === 'error' || saveStatus === 'idle') && <Save className="h-[18px] w-[18px]" />}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-[11px]">Save manuscript</TooltipContent>
        </Tooltip>

        {/* Sign Out */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button" disabled={savingOut} onClick={handleSaveAndSignOut}
              data-testid="activity-save-and-signout-btn"
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                hasUnsavedChanges && !savingOut
                  ? 'text-amber-400 bg-amber-500/10 hover:bg-amber-500 hover:text-white'
                  : 'text-chrome-foreground/70 hover:text-foreground hover:bg-chrome-border',
                savingOut && 'opacity-75',
              )}
            >
              {savingOut ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <LogOut className="h-[18px] w-[18px]" />}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-[11px]">{hasUnsavedChanges ? 'Unsaved changes — save & sign out' : 'Sign out'}</TooltipContent>
        </Tooltip>
      </div>

      {/* ── Three-Act + Business icon groups ─────────────────────────── */}
      <div className="flex flex-1 flex-col overflow-y-auto">
        {ACT_GROUPS.map((group, gi) => (
          <div
            key={group.act}
            data-testid={`act-section-${group.act}`}
            className={cn('flex flex-col items-center gap-0.5 py-2', gi > 0 && 'border-t border-chrome-border/60')}
          >
            {group.items.map((item) => {
              const Icon     = item.icon;
              const gated    = item.capability && !can(item.capability);
              const isActive = visible[item.key];
              const isCommunity   = item.key === 'community';
              const showDot       = isCommunity && unread > 0 && !isActive;
              const showMention   = isCommunity && mentions > 0 && !isActive;
              const showOpp       = item.key === 'marketing' && opportunityCount > 0 && !isActive;
              const showRebuildDot = !isActive && !!pendingByPanel[item.key];

              const handleClick = () => {
                if (gated) {
                  const cta = upgradeCta(item.capability);
                  toast.info(cta.title, { description: `${cta.body} · Upgrade in Settings → Subscription.`, duration: 4500 });
                  return;
                }
                if (item.action) { item.action(); return; }
                if (isCommunity) markRead();
                togglePanel(item.key);
              };

              return (
                <Tooltip key={item.key}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleClick}
                      data-testid={`activity-${item.key}-btn`}
                      className={cn(
                        'relative flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                        isActive ? 'bg-primary/15 text-primary' : 'text-chrome-foreground/60 hover:text-foreground hover:bg-chrome-border',
                        gated && 'opacity-40',
                      )}
                    >
                      {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r-full bg-primary" />}
                      <Icon className="h-[18px] w-[18px]" />
                      {/* Badge indicators */}
                      {showMention && (
                        <span className="absolute right-0.5 top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500 text-[7px] font-bold text-white" data-testid="activity-community-mention-badge">
                          <AtSign className="h-2 w-2" />
                        </span>
                      )}
                      {!showMention && showDot && (
                        <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-primary" data-testid="activity-community-unread-dot" />
                      )}
                      {showOpp && (
                        <span className="absolute right-0.5 top-0.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-primary px-0.5 text-[7px] font-bold text-primary-foreground" data-testid="activity-marketing-opportunities-badge">
                          {Math.min(opportunityCount, 9)}
                        </span>
                      )}
                      {gated && <Lock className="absolute right-0.5 bottom-0.5 h-2 w-2 text-chrome-foreground/40" data-testid={`activity-${item.key}-lock`} />}
                      {/* Amber rebuild-needed dot — shown when foundation data changed since last rebuild */}
                      {showRebuildDot && (
                        <span
                          className="absolute right-1 bottom-1 h-1.5 w-1.5 rounded-full bg-amber-400 shadow shadow-amber-400/60"
                          data-testid={`activity-${item.key}-rebuild-dot`}
                          title="Foundation changed — rebuild AI context"
                        />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="text-[11px]">
                    <span className="font-medium">{item.label}{gated ? ' · Pro Author' : ''}</span>
                    {isGuidance && item.guidanceTip && (
                      <p className="mt-0.5 max-w-[200px] text-[10px] leading-snug text-muted-foreground/80">{item.guidanceTip}</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        ))}
      </div>

      {/* ── Bottom utility icons ──────────────────────────────────────── */}
      <div className="flex flex-col items-center gap-0.5 border-t border-chrome-border py-2">
        {[
          { id: 'business',   Icon: TrendingUp, label: 'Business Dashboard', action: onOpenBusiness,    testId: 'activity-business-btn' },
          { id: 'framework',  Icon: Compass,    label: 'Novel Framework',    href:   '/framework',      testId: 'activity-framework-btn' },
          { id: 'quickstart', Icon: BookOpen,   label: 'Quickstart Guide',   action: onOpenQuickstart,  testId: 'activity-quickstart-btn', active: quickstartOpen },
          { id: 'process',    Icon: Sparkles,   label: 'The Process',        action: () => runTour('the-process', { force: true }), testId: 'activity-process-guide-btn' },
          { id: 'help',       Icon: HelpCircle, label: 'Context Help',       action: onOpenHelp,        testId: 'activity-context-help-btn' },
          { id: 'templates',  Icon: FileCode2,  label: 'Templates',          action: onOpenTemplates,   testId: 'activity-templates-btn' },
          { id: 'tokens',     Icon: Wallet,     label: 'Tokens & Billing',   action: onBuyTokens,       testId: 'open-buy-tokens-button' },
          { id: 'settings',   Icon: Cog,        label: 'Settings',           action: onSettings,        testId: 'open-settings-button' },
        ].map(({ id, Icon, label, action, href, testId, active }) => (
          <Tooltip key={id}>
            <TooltipTrigger asChild>
              {href ? (
                <a href={href} data-testid={testId}
                  className="flex h-9 w-9 items-center justify-center rounded-md text-chrome-foreground/60 transition-colors hover:text-foreground hover:bg-chrome-border">
                  <Icon className="h-[18px] w-[18px]" />
                </a>
              ) : (
                <button onClick={action} data-testid={testId}
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                    active ? 'text-primary bg-primary/10' : 'text-chrome-foreground/60 hover:text-foreground hover:bg-chrome-border',
                  )}>
                  <Icon className="h-[18px] w-[18px]" />
                </button>
              )}
            </TooltipTrigger>
            <TooltipContent side="right" className="text-[11px]">{label}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}

